import { useContext, useState } from 'react';
import * as R from 'ramda';

import AttributeDetails from '@atom/components/common/attributeDetails/AttributeDetails';
import textConstants from '@atom/constants/textConstants';
import { Button, Icon, Snackbar } from '@atom/mui';
import { MapIcon } from '@atom/mui/icons';
import colors from '@atom/styles/colors';
import { AssetAttribute } from '@atom/types/asset';
import { LocationUpdatePayload } from '@atom/types/map';
import { PolicyAction } from '@atom/types/policy';
import { AttributesType } from '@atom/types/schema';
import { hasAccess } from '@atom/utilities/accessUtilities';

import InventoryDetailContext from '../InventoryDetailContext';

import InventoryDetailMap from './InventoryDetailMap';

import classes from './infoTab.module.css';

const styles = {
  hideMapButton: {
    zIndex: '250',
    position: 'fixed',
    right: '1rem',
    top: '11rem',
    color: colors.neutral.white,
    display: 'flex',
    columnGap: '0.5rem',
  },
};

type AttributeUpdatePayload = {
  id: string;
  attributeGroupName: string;
  attributes: { [id: string]: AssetAttribute };
};

const InfoTab = () => {
  const {
    asset,
    updateAsset,
    refetch,
    approveAttributeChange,
    rejectAttributeChange,
  } = useContext(InventoryDetailContext);

  const [isMapHidden, setIsMapHidden] = useState<boolean>(false);
  const [loadingAttributeGroups, setLoadingAttributeGroups] = useState<
    string[]
  >([]);

  const onLocationUpdate = async (payload: LocationUpdatePayload) => {
    Snackbar.info({
      message: textConstants.GENERIC_APPLICATION_UPDATING_TEXT,
    });

    try {
      await updateAsset({
        id: asset.id,
        location: payload?.location,
      });

      Snackbar.info({
        message: textConstants.GENERIC_APPLICATION_UPDATED_TEXT,
        autoHideDuration: 3000,
      });
    } catch (error) {
      Snackbar.error({
        message: textConstants.GENERIC_APPLICATION_ERROR_TEXT,
      });
    }
  };

  const onPendingApproval = async (
    action: string,
    attributeGroupName: string,
    attribute: AttributesType,
  ) => {
    const input = {
      assetId: asset.id,
      attributeId: attribute.id,
      changeId: attribute.changeId,
      rootAssetId: asset.id,
    };

    Snackbar.info({
      message: textConstants.GENERIC_APPLICATION_UPDATING_TEXT,
    });

    try {
      if (action === 'approve') {
        await approveAttributeChange(input);
      } else {
        await rejectAttributeChange(input);
      }

      await refetch();

      Snackbar.info({
        message: textConstants.GENERIC_APPLICATION_UPDATED_TEXT,
        autoHideDuration: 3000,
      });
    } catch (error) {
      Snackbar.error({
        message: textConstants.GENERIC_APPLICATION_ERROR_TEXT,
      });
    }
  };

  const onAttributeUpdate = async (payload: AttributeUpdatePayload) => {
    setLoadingAttributeGroups(current => [
      ...current,
      payload.attributeGroupName,
    ]);

    Snackbar.info({
      message: textConstants.GENERIC_APPLICATION_UPDATING_TEXT,
    });

    try {
      await updateAsset({
        id: asset.id,
        attributes: R.map(
          attribute => ({ value: attribute.value }),
          payload.attributes,
        ),
      });

      Snackbar.info({
        message: textConstants.GENERIC_APPLICATION_UPDATED_TEXT,
        autoHideDuration: 3000,
      });
    } catch (error) {
      Snackbar.error({
        message: textConstants.GENERIC_APPLICATION_ERROR_TEXT,
      });
    } finally {
      setLoadingAttributeGroups(R.without([payload.attributeGroupName]));
    }
  };

  const canManageChanges = hasAccess(
    PolicyAction.MANAGE_INVENTORY_CHANGES,
    asset.actions,
  );
  const canUpdate = hasAccess(PolicyAction.UPDATE, asset.actions);
  const showMap = !R.isNil(asset.locationType);

  if (!showMap) {
    return (
      <div className={classes.centerPaneContainer}>
        <div className={classes.centerPane}>
          <AttributeDetails
            onPendingApproval={onPendingApproval}
            asset={asset}
            onAttributeUpdate={onAttributeUpdate}
            loading={loadingAttributeGroups}
            canManageChanges={canManageChanges}
            canUpdate={canUpdate}
            inputWidth="25vw"
          />
        </div>
      </div>
    );
  }

  return (
    <>
      <Button
        variant="text"
        style={styles.hideMapButton}
        data-cy="infoTabToggleMapButton"
        onClick={() => setIsMapHidden(current => !current)}
      >
        <Icon color={colors.neutral.white} icon={MapIcon} />
        {isMapHidden ? 'Show Map' : 'Hide Map'}
      </Button>
      <div
        className={
          isMapHidden ? classes.fullBodyPane : classes.leftBodyPaneHalf
        }
      >
        <AttributeDetails
          onPendingApproval={onPendingApproval}
          asset={asset}
          onAttributeUpdate={onAttributeUpdate}
          loading={loadingAttributeGroups}
          canManageChanges={canManageChanges}
          canUpdate={canUpdate}
          inputWidth="25vw"
          splitView={isMapHidden}
        />
      </div>
      {!isMapHidden && (
        <div className={classes.rightBodyPaneHalf}>
          <InventoryDetailMap onSave={onLocationUpdate} asset={asset} />
        </div>
      )}
    </>
  );
};

export default InfoTab;
